import {
  AdgroupConfig,
  AdgroupFormData,
  CampaignFormData,
  AppProfilePageType,
  StrategyListItem,
  AdgroupListItem,
  SmartBidType,
  CreateMode
} from '@tt4b/ttam-enums';
import { useAdgroupBidTypes, useAdgroupIos14, useAdgroupMode, useAllowlist, useDCAdgroup } from '@tt4b/ttam-hooks';
import { ref, Ref, computed } from '@vue/composition-api';

function useCommonIsAppProfilePageTypeOn(props: { adgroupData: Ref<AdgroupFormData> }) {
  return computed(() => props?.adgroupData?.value?.adAppProfilePageType === AppProfilePageType.on);
}

function useCommonIsModeSupportEditIcon(props: {
  adgroupData: Ref<AdgroupFormData>;
  campaignData: Ref<CampaignFormData>;
  dcAdgroupList: Ref<AdgroupListItem[]>;
  hasLowestCost: Ref<boolean>;
}) {
  const { adgroupData, campaignData, dcAdgroupList, hasLowestCost } = props;
  // TODO 待splittest支持
  const isSplitTestEnabled = ref(false);

  const isAppProfilePageTypeOn = useCommonIsAppProfilePageTypeOn({
    adgroupData,
  });

  const { isDcTwoAdgroup } = useAdgroupIos14({
    adgroupFormData: adgroupData,
    campaignFormData: campaignData,
  });

  const { isTwoHasNoAdgroupUnderDc, isTwoAdgroupUnderDc } = useDCAdgroup({
    campaignFormData: campaignData,
    adgroupFormData: adgroupData,
    dcAdgroupList,
  });

  const { isAppProfileAutoSelectedWhite } = useAllowlist();

  const isModeSupportEditIcon = computed(
    () =>
      // 完全新建，dc下无adgroup， dc下有adgroup且这个adgroup等于lowest
      ((isDcTwoAdgroup.value && !campaignData.value.campaignId) ||
        (isTwoAdgroupUnderDc.value && hasLowestCost.value) ||
        isTwoHasNoAdgroupUnderDc.value ||
        (isAppProfilePageTypeOn.value && !isAppProfileAutoSelectedWhite.value)) &&
      !isSplitTestEnabled.value
  );

  return isModeSupportEditIcon;
}

/** DcInfo  */
export function useDcInfoPropsVisible(props: {
  adgroupData: Ref<AdgroupFormData>;
  campaignData: Ref<CampaignFormData>;
  adgroupConfig: Ref<AdgroupConfig>;
  isDcEditClicked: Ref<boolean>;
  dcAdgroupList: Ref<AdgroupListItem[]>;
  hasLowestCost: Ref<boolean>;
  strategyList: Ref<StrategyListItem[]>;
}) {
  const { isDcEditClicked, adgroupConfig, strategyList, adgroupData } = props;
  const isModeSupportEditIcon = useCommonIsModeSupportEditIcon(props);
  const { isSupportLowestCost } = useAdgroupBidTypes({
    adgroupFormData: adgroupData,
    strategyList,
  });
  const isShowDcDefaultBidStrategy = computed(
    () =>
      !isDcEditClicked.value &&
      isSupportLowestCost.value &&
      isModeSupportEditIcon.value &&
      !adgroupConfig.value?.disableIsDcEditClicked
  );
  return {
    visible: computed(() => isShowDcDefaultBidStrategy.value),
  };
}

export function useDcInfoPropsEditButtonVisible(props: {
  adgroupData: Ref<AdgroupFormData>;
  adgroupConfig: Ref<AdgroupConfig>;
  strategyList: Ref<StrategyListItem[]>;
}) {
  const { adgroupConfig, strategyList } = props;
  const { isAppProfilePageSupportCostCap } = useAllowlist();
  const isAppProfilePageTypeOn = useCommonIsAppProfilePageTypeOn(props);
  const isEditButtonVisible = computed(
    () =>
      strategyList.value.length > 1 &&
      (!adgroupConfig.value?.disableIsDcEditClicked ||
        isAppProfilePageSupportCostCap.value ||
        !isAppProfilePageTypeOn.value)
  );
  return {
    visible: computed(() => isEditButtonVisible.value),
  };
}

/** DcTips  */
export function useDcTipsPropsVisible(props: {
  adgroupData: Ref<AdgroupFormData>;
  campaignData: Ref<CampaignFormData>;
  adgroupConfig: Ref<AdgroupConfig>;
  isDcEditClicked: Ref<boolean>;
  dcAdgroupList: Ref<AdgroupListItem[]>;
  hasLowestCost: Ref<boolean>;
  strategyList: Ref<StrategyListItem[]>;
  createMode: Ref<CreateMode>;
}) {
  const { isDcEditClicked, adgroupConfig, strategyList, adgroupData, createMode, hasLowestCost } = props;
  const isModeSupportEditIcon = useCommonIsModeSupportEditIcon(props);
  const { isSupportLowestCost, isCostCap } = useAdgroupBidTypes({
    adgroupFormData: adgroupData,
    strategyList,
  });
  const { isEdit } = useAdgroupMode({
    createMode,
  });
  const { isAppProfileAutoSelectedWhite } = useAllowlist();
  const isShowDcDefaultBidStrategy = computed(
    () =>
      !isDcEditClicked.value &&
      !isAppProfileAutoSelectedWhite.value &&
      isSupportLowestCost.value &&
      isModeSupportEditIcon.value &&
      !adgroupConfig.value?.disableIsDcEditClicked
  );
  return {
    visible: computed(
      () =>
        !isEdit.value &&
        !isShowDcDefaultBidStrategy.value &&
        isModeSupportEditIcon.value &&
        isCostCap.value &&
        strategyList.value.find(item => item.value === SmartBidType.NO_BID)
    ),
  };
}
